/**
 * PHSIMS Web Admin - Keycloak SSO Service
 * Author: Iswe Solutions Team
 * 
 * Keycloak authentication service for Single Sign-On.
 * Handles initialization, login, logout, and token management.
 */

import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js';

// API URL for fetching SSO configuration
const getApiUrl = () => {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  return '/api';
};

const API_URL = getApiUrl();

// Keycloak configuration from backend
interface SSOConfig {
  enabled: boolean;
  url: string;
  realm: string;
  clientId: string;
}

// User information from Keycloak token
interface KeycloakUser {
  id: string;
  email: string | undefined;
  username: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_superuser: boolean;
  roles: string[];
}

// Singleton Keycloak instance
let keycloakInstance: Keycloak | null = null;
let ssoConfig: SSOConfig | null = null;
let initPromise: Promise<boolean> | null = null;

/**
 * Check if running in a secure context where Web Crypto API is available.
 * PKCE requires Web Crypto API, which is only available on HTTPS or localhost.
 */
export const isSecureContext = (): boolean => {
  // Check browser's isSecureContext flag
  if (window.isSecureContext) return true;
  
  // Also allow localhost for development
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  
  // Check if Web Crypto API is actually available
  if (window.crypto?.subtle) return true;
  
  return false;
};

// Check if SSO is explicitly disabled via environment variable
const isSSODisabled = (): boolean => {
  const envDisabled = (import.meta as any).env?.VITE_SSO_DISABLED;
  return envDisabled === 'true' || envDisabled === '1';
};

/**
 * Fetch SSO configuration from backend with timeout
 * Returns disabled config immediately if VITE_SSO_DISABLED=true
 */
export const fetchSSOConfig = async (): Promise<SSOConfig> => {
  // Skip SSO check entirely if disabled via env var
  if (isSSODisabled()) {
    return { enabled: false, url: '', realm: '', clientId: '' };
  }
  
  if (ssoConfig) return ssoConfig;
  
  // Create abort controller for timeout - 10s is generous
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
  
  try {
    const response = await fetch(`${API_URL}/auth/sso/config`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error('Failed to fetch SSO config');
    }
    ssoConfig = await response.json();
    return ssoConfig!;
  } catch (error) {
    clearTimeout(timeoutId);
    // Only log if it's not an abort (timeout is expected in some cases)
    if (error instanceof Error && error.name !== 'AbortError') {
      console.warn('SSO config fetch failed:', error.message);
    }
    // Return disabled config on error or timeout - SSO just won't be available
    return { enabled: false, url: '', realm: '', clientId: '' };
  }
};

/**
 * Check if Keycloak SSO is enabled
 */
export const isKeycloakEnabled = async (): Promise<boolean> => {
  const config = await fetchSSOConfig();
  return config.enabled;
};

/**
 * Get or create Keycloak instance
 */
export const getKeycloak = async (): Promise<Keycloak | null> => {
  const config = await fetchSSOConfig();
  
  if (!config.enabled) {
    return null;
  }
  
  if (!keycloakInstance) {
    const keycloakConfig: KeycloakConfig = {
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    };
    keycloakInstance = new Keycloak(keycloakConfig);
  }
  
  return keycloakInstance;
};

/**
 * Initialize Keycloak
 * Call this on app startup to check for existing session
 */
export const initKeycloak = async (onAuthenticatedCallback?: () => void): Promise<boolean> => {
  // Check if we're in a secure context (HTTPS or localhost)
  // Web Crypto API required for PKCE is not available on plain HTTP
  if (!isSecureContext()) {
    console.log('Keycloak SSO requires HTTPS - skipping initialization on non-secure context');
    return false;
  }
  
  // Return existing promise if already initializing
  if (initPromise) return initPromise;
  
  initPromise = (async () => {
    const keycloak = await getKeycloak();
    
    if (!keycloak) {
      console.log('Keycloak SSO is disabled, using local auth');
      return false;
    }
    
    try {
      // Determine if we can use PKCE (requires Web Crypto API, only available on HTTPS or localhost)
      const isSecureContext = window.isSecureContext || 
                              window.location.protocol === 'https:' || 
                              window.location.hostname === 'localhost' ||
                              window.location.hostname === '127.0.0.1';
      
      const initOptions: KeycloakInitOptions = {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        // Only use PKCE on secure contexts (HTTPS or localhost)
        ...(isSecureContext ? { pkceMethod: 'S256' } : {}),
        checkLoginIframe: false,
      };
      
      const authenticated = await keycloak.init(initOptions);
      
      if (authenticated) {
        console.log('Keycloak: User authenticated');
        // Store tokens
        if (keycloak.token) {
          localStorage.setItem('auth_token', keycloak.token);
        }
        if (keycloak.refreshToken) {
          localStorage.setItem('refresh_token', keycloak.refreshToken);
        }
        
        // Extract user info from token
        const user = extractUserFromToken(keycloak);
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('auth_method', 'keycloak');
        }
        
        // Setup token refresh
        setupTokenRefresh(keycloak);
        
        onAuthenticatedCallback?.();
      } else {
        console.log('Keycloak: User not authenticated');
      }
      
      return authenticated;
    } catch (error) {
      console.error('Keycloak init failed:', error);
      return false;
    }
  })();
  
  return initPromise;
};

/**
 * Extract user information from Keycloak token
 */
const extractUserFromToken = (keycloak: Keycloak): KeycloakUser | null => {
  if (!keycloak.tokenParsed) return null;
  
  const token = keycloak.tokenParsed as any;
  
  // Extract roles from realm_access and resource_access
  const realmRoles = token.realm_access?.roles || [];
  const clientRoles = token.resource_access?.[ssoConfig?.clientId || '']?.roles || [];
  const allRoles = [...realmRoles, ...clientRoles];
  
  // Determine primary role
  const isAdmin = allRoles.some((r: string) => 
    ['admin', 'phsims-admin', 'realm-admin'].includes(r.toLowerCase())
  );
  
  let role = 'user';
  if (isAdmin) role = 'admin';
  else if (allRoles.includes('analyst')) role = 'analyst';
  else if (allRoles.includes('operator')) role = 'operator';
  else if (allRoles.includes('viewer')) role = 'viewer';
  
  return {
    id: token.sub,
    email: token.email,
    username: token.preferred_username || token.sub,
    full_name: token.name || token.given_name || token.preferred_username || 'User',
    role,
    is_active: true,
    is_superuser: isAdmin,
    roles: allRoles,
  };
};

/**
 * Setup automatic token refresh
 */
const setupTokenRefresh = (keycloak: Keycloak) => {
  // Refresh token 1 minute before expiry
  setInterval(async () => {
    if (keycloak.authenticated) {
      try {
        const refreshed = await keycloak.updateToken(60);
        if (refreshed) {
          console.log('Keycloak: Token refreshed');
          if (keycloak.token) {
            localStorage.setItem('auth_token', keycloak.token);
          }
          if (keycloak.refreshToken) {
            localStorage.setItem('refresh_token', keycloak.refreshToken);
          }
        }
      } catch (error) {
        console.error('Keycloak: Failed to refresh token:', error);
        // Clear auth and redirect to login
        clearAuth();
        keycloak.login();
      }
    }
  }, 30000); // Check every 30 seconds
};

/**
 * Login with Keycloak
 * Creates a new Keycloak instance and redirects to the login page
 */
export const keycloakLogin = async (redirectUri?: string) => {
  const config = await fetchSSOConfig();
  
  if (!config.enabled) {
    console.error('Keycloak SSO is not enabled');
    return;
  }
  
  // Check if we can use PKCE (requires secure context)
  const isSecureContext = window.isSecureContext || 
                          window.location.protocol === 'https:' || 
                          window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1';
  
  // Create a fresh Keycloak instance for login redirect
  const keycloakConfig: KeycloakConfig = {
    url: config.url,
    realm: config.realm,
    clientId: config.clientId,
  };
  
  const keycloak = new Keycloak(keycloakConfig);
  
  // Initialize with login-required to force redirect
  try {
    await keycloak.init({
      onLoad: 'login-required',
      redirectUri: redirectUri || window.location.origin + '/',
      // Only use PKCE on secure contexts (HTTPS or localhost)
      ...(isSecureContext ? { pkceMethod: 'S256' } : {}),
      checkLoginIframe: false,
    });
  } catch (error) {
    console.error('Keycloak login failed:', error);
    throw error;
  }
};

/**
 * Logout from Keycloak
 */
export const keycloakLogout = async (redirectUri?: string) => {
  const keycloak = await getKeycloak();
  
  // Clear local storage
  clearAuth();
  
  if (keycloak && keycloak.authenticated) {
    await keycloak.logout({
      redirectUri: redirectUri || window.location.origin + '/login',
    });
  }
};

/**
 * Clear authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth_method');
  // Clear server-side httpOnly cookie (fire-and-forget — non-blocking)
  fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
};

/**
 * Get current authentication method
 */
export const getAuthMethod = (): 'keycloak' | 'local' | null => {
  return localStorage.getItem('auth_method') as 'keycloak' | 'local' | null;
};

/**
 * Check if user is authenticated via Keycloak
 */
export const isKeycloakAuthenticated = async (): Promise<boolean> => {
  const keycloak = await getKeycloak();
  return keycloak?.authenticated ?? false;
};

/**
 * Get current Keycloak token
 */
export const getKeycloakToken = async (): Promise<string | null> => {
  const keycloak = await getKeycloak();
  if (keycloak?.authenticated && keycloak.token) {
    // Refresh if needed
    try {
      await keycloak.updateToken(30);
      return keycloak.token;
    } catch {
      return null;
    }
  }
  return null;
};

export default {
  fetchSSOConfig,
  isKeycloakEnabled,
  getKeycloak,
  initKeycloak,
  keycloakLogin,
  keycloakLogout,
  clearAuth,
  getAuthMethod,
  isKeycloakAuthenticated,
  getKeycloakToken,
};
